import React, { useMemo } from 'react';
import isNil from 'lodash/isNil';
import {
  IMeasureFieldsFragment,
  SearchNamedMeasureOption,
  TagOption,
} from '../../../../utils/apollo/graphqlSchema';
import EntitySelection from '../../EntitySelection';
import {
  asSingleSelection,
  createTagOptionInput,
} from '../../EntitySelection/utils';
import {
  IEntitySelectionLabelProps,
  ISelectedEntity,
  ISelectEntityHandler,
} from '../../EntitySelection/types';
import { assertCondition } from '../../../../utils/types';
import { useAppSelector } from '../../../../redux/hooks';
import { activeSchemaIdSelector } from '../../../../redux/selectors/builder';

interface IProps {
  defaultNamedMeasure: IMeasureFieldsFragment | null;
  onChange: (measure: IMeasureFieldsFragment) => void;
  onClear: () => void;
  autoFocus?: boolean;
  allowRollingControl: boolean;
  allowCompoundMeasure: boolean;
  onExpand?: () => void;
  isError?: boolean;
  isDisabled?: boolean;
}

export default ({
  defaultNamedMeasure,
  onChange,
  onClear,
  autoFocus,
  allowRollingControl,
  allowCompoundMeasure,
  onExpand,
  isError,
  isDisabled,
}: IProps) => {
  const activeSchemaId = useAppSelector(activeSchemaIdSelector);

  const tags = useMemo(() => {
    const measureOption = allowRollingControl
      ? SearchNamedMeasureOption.All
      : SearchNamedMeasureOption.NoRollingControl;

    const tags = [
      createTagOptionInput(TagOption.SimpleMeasure, {
        measure: {
          measureOption,
        },
      }),
    ];

    if (allowCompoundMeasure) {
      tags.push(
        createTagOptionInput(TagOption.CompoundMeasure, {
          measure: {
            measureOption,
          },
        }),
      );
    }
    return tags;
  }, [allowCompoundMeasure, allowRollingControl]);

  const onSelectEntityHandler: ISelectEntityHandler = (
    selectedEntity: ISelectedEntity,
  ) => {
    assertCondition(
      selectedEntity.__typename === 'SimpleMeasure' ||
        selectedEntity.__typename === 'CompoundMeasure',
    );
    onChange(selectedEntity);
  };

  const labelProps: IEntitySelectionLabelProps[] = useMemo(
    () =>
      !isNil(onExpand)
        ? [
            {
              type: 'Measure',
              onExpand,
            },
          ]
        : [],
    [onExpand],
  );

  return (
    <EntitySelection
      tags={tags}
      schemaId={activeSchemaId}
      selectedEntities={asSingleSelection(defaultNamedMeasure)}
      onSelectEntity={onSelectEntityHandler}
      placeholder="Search and select measure"
      onRemoveEntity={onClear}
      labelProps={labelProps}
      autoFocus={autoFocus}
      isError={isError}
      isDisabled={isDisabled}
    />
  );
};
